// @ts-nocheck

// App
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

// Components
import BedButtons from 'app/shared/modules/MobileSRP/Header/FilterModules/BedBathComboFilter/BedButtons';
import BathButtons from 'app/shared/modules/MobileSRP/Header/FilterModules/BedBathComboFilter/BathButtons';
import Checkbox from 'app/shared/modules/MobileSRP/__BaseComponents__/Checkbox';

// Hooks
import { useFilterChange } from 'app/shared/modules/MobileSRP/Header/FilterBar/hooks';

// Misc / utils
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/MobileSRP/Header/FilterBar/styles';
import { DEFAULT } from 'app/shared/models/Filter';
import {
  emitTrackBathroomFilterEvent,
  emitTrackBedroomFilterEvent,
} from 'app/shared/models/Clickstream/FilterClickstreamEvents';
import type { RootReduxState } from 'app/shared/flux/reducers';

export interface BedBathComboFilterProps {
  showResetButton?: boolean;
  hideBath?: boolean;
  preventDefault?: boolean;
  customTrackingEvent?: string | null;
  triggerLocation?: string | null;
  triggerObject?: string | null;
}

// This aids with the radio button feel of this component (i.e. only one item can be true; the rest are false)
const FALSY_STATE = {
  bedrooms: {
    anyOrStudio: false,
    oneOrMore: false,
    twoOrMore: false,
    threeOrMore: false,
    fourOrMore: false,
  },
  bathrooms: {
    any: false,
    oneOrMore: false,
    oneHalfOrMore: false,
    twoOrMore: false,
    threeOrMore: false,
    fourOrMore: false,
  },
};

/**
 * Bed & Bath selector - A component that contains display and business logic for the Bed and Baths filter
 * @param {Object} props - props object
 * @param {boolean} props.showResetButton [showResetButton=false] - Show or hide reset button
 * @param {boolean} props.hideBath [hideBath=false] - Show or hide bathroom selector
 * @param {boolean} props.preventDefault [preventDefault=false] - if true, only carry out Redux filter change (do not fecth listings)
 * @param {string} props.customTrackingEvent [customTrackingEvent=null] - if present, use event instead of default filter event
 * @returns component
 */
const BedBathComboFilter = ({
  showResetButton = false,
  hideBath = false,
  preventDefault = false,
  customTrackingEvent = null,
  triggerLocation = null,
  triggerObject = null,
}: BedBathComboFilterProps) => {
  // Redux
  const bedState = useSelector((state: RootReduxState) => state.filter.bedrooms);
  const bathState = useSelector((state: RootReduxState) => state.filter.bathrooms);
  const [filterState, setFilterState] = useState({ bedrooms: { ...bedState }, bathrooms: { ...bathState } });

  // React state
  const [isLoaded, setIsLoaded] = useState(false);

  // Hooks
  const { debouncedFilterChange, reduxOnlyChange, resetFilter, isFilterResetting } = useFilterChange();

  const handleResetClick = useYieldCallback(() => {
    resetFilter({ bedrooms: DEFAULT.bedrooms, bathrooms: DEFAULT.bathrooms, preventDefault });
    setFilterState({
      bedrooms: { ...DEFAULT.bedrooms },
      bathrooms: { ...DEFAULT.bathrooms },
    });
  });

  // Bedrooms
  const yieldHandleBedroomOptionClick = useYieldCallback((option) => {
    const change = Object.assign({}, FALSY_STATE.bedrooms, { isExact: filterState.bedrooms.isExact });
    change[option] = !filterState.bedrooms[option];

    setFilterState({
      bathrooms: {
        ...filterState.bathrooms,
      },
      bedrooms: {
        ...change,
      },
    });

    emitTrackBedroomFilterEvent({ triggerLocation, triggerObject });
  });

  const handleBedroomOptionClick = (e) => {
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    yieldHandleBedroomOptionClick(option);
  };

  const handleBedCheckboxClick = useYieldCallback(() => {
    setFilterState({
      bathrooms: {
        ...filterState.bathrooms,
      },
      bedrooms: {
        ...filterState.bedrooms,
        isExact: !filterState.bedrooms.isExact,
      },
    });

    emitTrackBedroomFilterEvent({ triggerLocation, triggerObject });
  });

  // Bathrooms
  const yieldHandleBathroomOptionClick = useYieldCallback((option) => {
    const change = Object.assign({}, FALSY_STATE.bathrooms);
    change[option] = !filterState.bathrooms[option];

    setFilterState({
      bedrooms: {
        ...filterState.bedrooms,
      },
      bathrooms: {
        ...change,
      },
    });

    emitTrackBathroomFilterEvent({ triggerLocation, triggerObject });
  });

  const handleBathroomOptionClick = (e) => {
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    yieldHandleBathroomOptionClick(option);
  };

  useEffect(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    } else {
      if (!isFilterResetting) {
        if (preventDefault) {
          reduxOnlyChange({ ...filterState });
        } else {
          reduxOnlyChange({ ...filterState });
        }
      }
    }
  }, [filterState, isLoaded, isFilterResetting, preventDefault]);

  return (
    <>
      <GeneralFilterSection>
        <GeneralFilterHeader>
          <GeneralFilterLabel id="bedroom-filter">Bedrooms</GeneralFilterLabel>
          {showResetButton && (
            <GeneralFilterResetButton
              onClick={handleResetClick}
              aria-label={'Reset all active bedroom and bathroom filters'}
            >
              Reset
            </GeneralFilterResetButton>
          )}
        </GeneralFilterHeader>
        <BedButtons handleClick={handleBedroomOptionClick} state={filterState.bedrooms} />
        <Checkbox
          onChange={handleBedCheckboxClick}
          isChecked={filterState.bedrooms.isExact}
          ariaLabel={'Use exact number of bedrooms'}
        >
          Use exact number of bedrooms
        </Checkbox>
      </GeneralFilterSection>
      {!hideBath && (
        <GeneralFilterSection>
          <GeneralFilterHeader>
            <GeneralFilterLabel id="bathroom-filter">Bathrooms</GeneralFilterLabel>
          </GeneralFilterHeader>
          <BathButtons handleClick={handleBathroomOptionClick} state={filterState.bathrooms} />
        </GeneralFilterSection>
      )}
    </>
  );
};

export default BedBathComboFilter;
